import axios from 'axios'
import { toast } from 'sonner'
import React, { useEffect, useState } from 'react'
import exportData from '../../../middlewares/Warehouse/exportToXlsx'

import { Tooltip, Spinner } from "@material-tailwind/react"
import { ArrowDownTrayIcon, ArrowPathIcon, ArrowPathRoundedSquareIcon, CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, EyeIcon, InformationCircleIcon, TrashIcon } from "@heroicons/react/24/solid"
import { BiLoaderCircle } from "react-icons/bi";
import { Link } from 'react-router-dom'
import DataTableShell from '../../../Components/table/DataTableShell'
import TableToolbar from '../../../Components/table/TableToolbar'
import TableSearch from '../../../Components/table/TableSearch'
import { TableActionGroup, TableEmptyRow, TableIdCell, TableSkeletonRows } from '../../../Components/table/TableCells'
import useClientTable from '../../../Components/table/useClientTable'

const TABLE_HEAD = ["Batch ID", "Uploaded By", "Category", "Status", "Actions"]

export function TransactionsTable({ isAltereds }) {

    const [downloadingBatch, setDownloadingBatch] = useState({});
    const [batchList, setBatchList] = useState([])
    const [isAltered, setIsAltered] = useState(false)
    const [batchContentData, setBatchContentData] = useState({})

    const batchesPerPage = 10
    const {
        searchTerm,
        handleSearch,
        currentPage,
        setCurrentPage,
        pageItems: batchData,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: batchList,
        pageSize: batchesPerPage,
        filterFn: (item, query) =>
            !query ||
            item.batchId?.toString().toLowerCase().includes(query) ||
            item.addedBy?.toLowerCase().includes(query),
        sortFn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    })

    useEffect(() => {
        const fetchBatchContent = async (batchId) => {
            try {
                const response = await axios.get(`https://api-central.outdoorequippedservice.com/api/v1/warehouse-refunds/audits/batch/processed-items/${batchId}`)
                const batchContentAc = response.data
                setBatchContentData(prevData => ({ ...prevData, [batchId]: batchContentAc }))
            } catch (error) {
                console.error('Error fetching batch content:', error)
                setBatchContentData(prevData => ({ ...prevData, [batchId]: [] }))
            }
        }
        batchData.forEach(({ batchId, status }) => {
            if (status.name === 'in-progress' || status.name === 'completed') {
                fetchBatchContent(batchId)
            }
        })
        // batchData is recreated during render, so depending on it would refetch continuously.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAltered])

    useEffect(() => {
        const toggleInterval = setInterval(() => {
            setIsAltered(prevState => !prevState)
        }, 2000) 
        return () => {
            clearInterval(toggleInterval)
        }
    }, [])

    useEffect(() => {
        axios.get('/api/refund-audits/all-batches')
            .then(response => {
                if (response.data.message) {
                    console.log("Cannot get batches")
                } else {
                    setBatchList(response.data.result)
                } 
            })
            .catch(error => {
                console.error('Error:', error)
            })
    }, [isAltered, isAltereds])

    const handleExportData = async (batchId, category) => {
        setDownloadingBatch(prevState => ({ ...prevState, [batchId]: true }));
        await exportData(batchId, category);
        setDownloadingBatch(prevState => ({ ...prevState, [batchId]: false }));
    }

    const handleReopenBatch = (batchId) => {

        const updatedStatus = {
            status: {
                name: 'open',
                message: 'OK!'
            }
        };
        
        axios.put(`/api/refund-audits/update/${batchId}`, updatedStatus)
            .then(response => {
                if(response.data.message) {
                    setIsAltered(prevState => !prevState)
                    console.log('repoening', batchId)
                    axios.delete(`https://api-central.outdoorequippedservice.com/api/v1/warehouse-refunds/audits/batch/${batchId}`)
                    .then(response => {console.log(response.data.result)})
                }else {
                    toast.error(`${response.data.message}`)
                    console.log('Error:', response.data.error)
                }
            })
    }

    const handleDeleteBatch = (batchId) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete this batch: ${batchId}?`)

        if (isConfirmed) {
            axios.delete(`/api/refund-audits/delete/${batchId}`)
                .then(response => {
                    if (response.data.message) {
                        toast.success(`${response.data.message}`)
                        setIsAltered(prevState => !prevState)
                        axios.delete(`https://api-central.outdoorequippedservice.com/api/v1/warehouse-refunds/audits/batch/${batchId}`)
                        .then(response => {console.log(response.data.result)})
                    } else {
                        toast.error(`${response.data.message}`)
                        console.log('Error:', response.data.error)
                    }
                })
                .catch(error => {
                    console.error('Error:', error)
                }) 
        }
    };

    return (
        <DataTableShell
            minWidth="920px"
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            toolbar={
                <TableToolbar
                    kicker="Warehouse refunds"
                    title="Transaction batches"
                    subtitle={`${totalFiltered} matching of ${batchList.length} total batches`}
                    actions={
                        <div className="flex items-center gap-3">
                            <TableSearch
                                id="warehouse-batch-search"
                                value={searchTerm}
                                onChange={handleSearch}
                                placeholder="Search batch or uploader"
                            />
                            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted">
                                <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
                                Auto refresh · 2s
                                <div className="three-body">
                                    <div className="three-body__dot"></div>
                                    <div className="three-body__dot"></div>
                                    <div className="three-body__dot"></div>
                                </div>
                            </div>
                        </div>
                    }
                />
            }
        >
                <thead>
                <tr>
                    {TABLE_HEAD.map((head) => (
                    <th key={head}>{head}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                    { batchList.length <= 0 ? (
                        <TableSkeletonRows columns={TABLE_HEAD.length} rows={9} />
                    ) : batchData.length === 0 ? (
                        <TableEmptyRow
                            columns={TABLE_HEAD.length}
                            title="No transaction batches found"
                            description="Try a different batch ID or uploader."
                        />
                )  :                   
                    (batchData.map(({ batchId, status, addedBy, createdAt, batchContent, categories }) => {
                        const batchContentAc = batchContentData[batchId] || '0'
                        const date = new Date(createdAt)
                        const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                        
                        const options = { month: 'long', day: 'numeric', year: 'numeric' }
                        const formattedDate = date.toLocaleDateString('en-US', options)
                        const formattedDateTime = `${formattedDate} ${formattedTime}`

                        const categoryLabel = 
                        categories.includes('manageInventory') && categories.includes('manageOrders') ? 'Manage Inventory, Manage Orders'
                        : categories.includes('manageInventory') && categories.includes('manageReturns') ? 'Manage Inventory, Manage Returns'
                        : categories.includes('manageOrders') && categories.includes('manageReturns') ? 'Manage Orders, Manage Returns'
                        : categories.includes('manageInventory') ? 'Manage Inventory'
                        : categories.includes('manageOrders') ? 'Manage Orders'
                        : categories.includes('manageReturns') ? 'Manage Returns'
                        : 'All'

                        const refundAction = categories.includes('all') ? true : false

                        return (
                            <tr key={batchId}>
                                <td>
                                    <div className="flex items-center gap-1">
                                        <TableIdCell>{batchId}</TableIdCell>
                                        <Tooltip content={formattedDateTime}>
                                            <InformationCircleIcon className='h-4 w-4 text-muted'/>
                                        </Tooltip>
                                    </div>
                                </td>
                                <td>{addedBy}</td>
                                <td>{categoryLabel}</td>
                                <td>
                                    <div className="font-medium">
                                        {status.name === 'open' ? 
                                            <div className='flex items-center gap-2 text-signal'>
                                                Open <ExclamationCircleIcon className='h-5 w-5'/>
                                            </div>
                                            : status.name === 'completed' ? 
                                            <div className='flex items-center gap-2 text-success'>
                                                Completed <CheckCircleIcon className='h-5 w-5'/>
                                            { batchContentAc === '0' ? <Spinner color='blue' className='h-5 w-5'/> :
                                            <span className="font-mono text-xs text-paper">{batchContentAc}/{batchContent.length}</span>
                                            }
                                            </div>
                                            : status.name === 'in-progress' ?                            
                                            <div className='flex items-center gap-2 text-warning'>
                                                Processing <ArrowPathIcon className='h-5 w-5 animate-spin'/> 
                                                <span className='animate-pulse font-mono text-xs text-paper'>{batchContentAc}/{batchContent.length}</span>
                                            </div>
                                            : status.name === 'reset' ?                            
                                            <div className='flex items-center gap-2 animate-pulse text-warning'>
                                                Resetting <ArrowPathIcon className='h-5 w-5'/> 
                                            </div>                                   
                                            : 
                                            <div className='flex items-center gap-2 text-danger'>
                                                Error 
                                                <Tooltip content={status.message}>
                                                <ExclamationTriangleIcon className='h-5 w-5 animate-pulse'/>
                                                </Tooltip>
                                            </div>
                                        }
                                    </div>
                                </td>
                                <td>
                                  <TableActionGroup>
                                    <Tooltip content="Refund Actions">
                                        <Link to={`/warehouse-tools/warehouse-refunds/refund-actions/${batchId}`}>
                                            <button 
                                            type="button"
                                            disabled={!refundAction}
                                            className={`nx-btn-ghost p-2 text-signal disabled:cursor-not-allowed disabled:opacity-40 ${status.name !== 'completed' ? "hidden" : ''}`}>
                                            <EyeIcon className="h-4 w-4" />
                                            </button>  
                                        </Link>                   
                                    </Tooltip>
                                    <Tooltip content="Download file">
                                        <button type="button" className={`nx-btn-ghost p-2 text-signal ${status.name !== 'completed' ? "hidden" : ''}`} 
                                        onClick={() => handleExportData(batchId, categories)}>
                                        {downloadingBatch[batchId] ? <BiLoaderCircle className='h-4 w-4 animate-spin'/> : <ArrowDownTrayIcon className="h-4 w-4" />}
                                        </button>                      
                                    </Tooltip>
                                    {status.name !== 'open' ? (
                                        <Tooltip content="Reopen Batch">
                                            <button type="button" className="nx-btn-ghost p-2 text-warning" 
                                            onClick={() => handleReopenBatch(batchId)}
                                            >
                                                <ArrowPathRoundedSquareIcon className="h-4 w-4" />
                                            </button>                      
                                        </Tooltip>                  
                                    ) : ""}
                                    <Tooltip content="Delete Batch">
                                        <button type="button" className="nx-btn-ghost p-2 text-danger" onClick={() => handleDeleteBatch(batchId)}>
                                            <TrashIcon className="h-4 w-4" />
                                        </button>                      
                                    </Tooltip>
                                  </TableActionGroup>
                                </td>
                            </tr>
                        )
                    }))
                }
                </tbody>
        </DataTableShell>
    )
}
